import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateWithTimezone } from "../../../../../agent/src/common/time";
import ConversationTimeline, { type TimelineItem } from "../../leads/chat/ConversationTimeline";
import { type RouterOutputs } from "../../../trpc";
import { lead } from "../../../types";
import { trpc } from "../../../trpc";
import { usePipeline } from "../../../contexts/PipelineContext";

type ListingRequestDetail = RouterOutputs["listingRequest"]["get"];

type ListingRequestDetailPanelProps = {
  request?: ListingRequestDetail;
  leadData?: lead;
  isLoading: boolean;
  merchantId: string;
  country?: string;
  onLeadUpdate?: () => void;
};

const ListingRequestDetailPanel = ({
  request,
  isLoading,
  merchantId,
  country = "CO",
}: ListingRequestDetailPanelProps) => {
  const { allPositions } = usePipeline();

  // Fetch conversation timeline
  const { data: timelineData, isLoading: isLoadingTimeline } =
    trpc.conversations.listConversationTimeline.useQuery(
      { merchantId, leadId: request?.leadId || "" },
      {
        enabled: !!merchantId && !!request?.leadId,
      }
    );

  const timelineItems: TimelineItem[] = (timelineData as TimelineItem[] | undefined) || [];

  const getListingTypeBadge = (type: string) => {
    switch (type) {
      case "rent":
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
            Arriendo
          </span>
        );
      case "sale":
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
            Venta
          </span>
        );
      case "rent_and_sale":
        return (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800">
            Arriendo y Venta
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800">
            {type}
          </span>
        );
    }
  };

  const getStatusBadge = (status: "ocupado" | "desocupado") => {
    return status === "ocupado" ? (
      <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
        Ocupado
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-semibold text-teal-800">
        Desocupado
      </span>
    );
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      studio: "Estudio",
      apartment: "Apartamento",
      warehouse: "Bodega",
      house: "Casa",
      medical_office: "Consultorio",
      building: "Edificio",
      farm: "Finca",
      room: "Habitación",
      hotel: "Hotel",
      commercial_space: "Local Comercial",
      lot: "Lote",
      office: "Oficina",
      parking: "Parqueadero",
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="flex h-full flex-row overflow-hidden">
      {/* Left Panel: Request Details */}
      <div className="w-full shrink-0 overflow-y-auto border-r border-gray-200 bg-white md:min-w-[380px] lg:w-[380px]">
        <div className="p-6">
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="mb-2 text-xl font-bold">
                Solicitud #{request.id.slice(0, 8)}
              </h2>
              <p className="mb-4 text-xs text-gray-500">
                {formatDateWithTimezone(request.createdAt, country)}
              </p>

              <div className="mb-4 flex flex-wrap gap-2">
                {getListingTypeBadge(request.listingType)}
                {getStatusBadge(request.status)}
              </div>
            </div>

            <Separator />

            {/* Property Information */}
            <div>
              <p className="mb-3 text-sm font-semibold text-gray-700">
                Información del Inmueble
              </p>
              <div className="flex flex-col gap-2">
                <div>
                  <p className="mb-1 text-xs text-gray-600">Tipo de Propiedad:</p>
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800">
                    {getPropertyTypeLabel(request.propertyType)}
                  </span>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-600">Ciudad:</p>
                  <p className="text-sm font-semibold">{request.city}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs text-gray-600">Barrio:</p>
                  <p className="text-sm font-semibold">{request.neighborhood}</p>
                </div>
                {request.address && (
                  <div>
                    <p className="mb-1 text-xs text-gray-600">Dirección:</p>
                    <p className="text-sm font-semibold">{request.address}</p>
                  </div>
                )}
                {request.isInCoveredArea !== undefined && (
                  <div>
                    <p className="mb-1 text-xs text-gray-600">Zona de Cobertura:</p>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                        request.isInCoveredArea
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      )}
                    >
                      {request.isInCoveredArea ? "Sí" : "No"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Client Information */}
            {request.lead && (
              <div>
                <p className="mb-3 text-sm font-semibold text-gray-700">
                  Información del Cliente
                </p>
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="mb-1 text-xs text-gray-600">Nombre:</p>
                    <p className="text-sm font-semibold">{request.lead.name}</p>
                  </div>
                  {request.lead.phone && (
                    <div>
                      <p className="mb-1 text-xs text-gray-600">Teléfono:</p>
                      <p className="text-sm font-semibold">{request.lead.phone}</p>
                    </div>
                  )}
                  {request.lead.email && (
                    <div>
                      <p className="mb-1 text-xs text-gray-600">Email:</p>
                      <p className="text-sm font-semibold">{request.lead.email}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel: Conversation Interface */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-[#F9F9FB]">
        <div className="p-4">
          <div className="rounded-md bg-white shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
            {request.leadId ? (
              <ConversationTimeline
                timelineData={timelineItems}
                isLoading={isLoadingTimeline}
                allPositions={allPositions}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-gray-500">No hay conversación disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingRequestDetailPanel;
